export class LocalStorageHelper {
    private static localStorage: { [key: string]: string } = {};

    private isTestEnvironment(): boolean {
        return typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
    }

    private isLocalStorageAvailable(): boolean {
        try {
            const test = '__localStorage_test__';
            localStorage.setItem(test, test);
            localStorage.removeItem(test);
            return true;
        } catch (e) {
            return false;
        }
    }

    getItem(key: string): string | null {
        if (this.isTestEnvironment()) {
            return LocalStorageHelper.localStorage[key] || null;
        }

        if (!this.isLocalStorageAvailable()) {
            console.warn('LocalStorage is not available');
            return null;
        }

        try {
            return localStorage.getItem(key);
        } catch (error) {
            console.error('Error getting item from localStorage:', error);
            return null;
        }
    }

    setItem(key: string, value: string): void {
        if (this.isTestEnvironment()) {
            LocalStorageHelper.localStorage[key] = value;
            console.log("localStorage item set in test environment");
            return;
        }

        if (!this.isLocalStorageAvailable()) {
            console.warn('LocalStorage is not available');
            return;
        }

        try {
            localStorage.setItem(key, value);
        } catch (error) {
            console.error('Error setting item in localStorage:', error);
        }
    }

    removeItem(key: string): void {
        if (this.isTestEnvironment()) {
            delete LocalStorageHelper.localStorage[key];
            return;
        }

        if (!this.isLocalStorageAvailable()) {
            console.warn('LocalStorage is not available');
            return;
        }

        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.error('Error removing item from localStorage:', error);
        }
    }

    removeItems(keys: string[]): void {
        keys.forEach((key) => {
            this.removeItem(key);
        });
    }

    clear(): void {
        if (this.isTestEnvironment()) {
            LocalStorageHelper.localStorage = {};
            return;
        }

        if (!this.isLocalStorageAvailable()) {
            console.warn('LocalStorage is not available');
            return;
        }

        try {
            localStorage.clear();
        } catch (error) {
            console.error('Error clearing localStorage:', error);
        }
    }

    getObject<T>(key: string): T | null {
        const item = this.getItem(key);
        if (item === null) {
            return null;
        }

        try {
            return JSON.parse(item) as T;
        } catch (error) {
            console.error('Error parsing JSON from localStorage:', error);
            return null;
        }
    }

    setObject<T>(key: string, value: T): void {
        try {
            const serializedValue = JSON.stringify(value);
            this.setItem(key, serializedValue);
        } catch (error) {
            console.error('Error serializing object to localStorage:', error);
        }
    }

    hasItem(key: string): boolean {
        return this.getItem(key) !== null;
    }

    getAllKeys(): string[] {
        if (this.isTestEnvironment()) {
            return Object.keys(LocalStorageHelper.localStorage);
        }

        if (!this.isLocalStorageAvailable()) {
            console.warn('LocalStorage is not available');
            return [];
        }

        try {
            const keys: string[] = [];
            for (let i = 0; i < localStorage.length; i++) {
                const key = localStorage.key(i);
                if (key !== null) {
                    keys.push(key);
                }
            }
            return keys;
        } catch (error) {
            console.error('Error getting keys from localStorage:', error);
            return [];
        }
    }

    getSize(): number {
        if (this.isTestEnvironment()) {
            return Object.keys(LocalStorageHelper.localStorage).length;
        }

        if (!this.isLocalStorageAvailable()) {
            console.warn('LocalStorage is not available');
            return 0;
        }

        try {
            return localStorage.length;
        } catch (error) {
            console.error('Error getting localStorage size:', error);
            return 0;
        }
    }

    private static instance: LocalStorageHelper;
    public static Instance(): LocalStorageHelper {
        if (!this.instance) {
            this.instance = new LocalStorageHelper();
        }
        return this.instance;
    }
}
